import { useState } from 'react'
import { ClipboardCheck, ChevronDown, ChevronRight, AlertTriangle, Loader2, RefreshCw, CheckCircle, XCircle, RotateCcw, Search, BellOff, HelpCircle, MessageSquare, Zap, X, ArrowRight } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useEngReview, useEngReviewAction } from '@/hooks/useApi'
import type { EngReviewRec } from '@/lib/api'

const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
  medium: { label: 'Medium', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  low: { label: 'Low', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
} as const

const CATEGORY_LABELS: Record<string, string> = {
  conflict: 'Conflict',
  stale: 'Stale',
  missing: 'Missing',
  missing_implementation: 'Missing Impl.',
  naming: 'Naming',
  pollution: 'Pollution',
  direct_question: 'Direct Question',
}

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-accent/15 text-accent border-accent/30' },
  accepted: { label: 'Accepted', color: 'bg-green-500/15 text-green-400 border-green-500/30' },
  accepted_pending_execution: { label: 'Queued', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  declined: { label: 'Declined', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
  more_research: { label: 'Research', color: 'bg-purple-500/15 text-purple-400 border-purple-500/30' },
  snoozed: { label: 'Snoozed', color: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
  completed: { label: 'Executed', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
} as const

const STATUS_FILTERS = ['all', 'open', 'more_research', 'accepted', 'accepted_pending_execution', 'completed', 'declined', 'snoozed'] as const
const PRIORITY_FILTERS = ['all', 'high', 'medium', 'low'] as const

type PendingAction = { action: string; notes: string; snoozeUntil: string }

type ExecStep = {
  tool: string
  args?: Record<string, unknown>
  path?: string
  description?: string
}

function timeSince(iso: string) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 3600) return Math.floor(s / 60) + 'm ago'
  if (s < 86400) return Math.floor(s / 3600) + 'h ago'
  return Math.floor(s / 86400) + 'd ago'
}

function defaultSnoozeDate() {
  const d = new Date()
  d.setDate(d.getDate() + 7)
  return d.toISOString().slice(0, 10)
}

function shortPath(p: string) {
  return p.split('/').pop() || p
}

function StepDetail({ step }: { step: ExecStep }) {
  const { tool, args = {}, path: stepPath, description } = step

  if (description) return <span>{description}</span>

  switch (tool) {
    case 'move_file': {
      const src = (args.source_path as string) || ''
      const dst = (args.destination_path as string) || ''
      return (
        <span>
          Rename in GitHub:{' '}
          <span className="font-mono text-text-muted">{shortPath(src)}</span>
          <ArrowRight className="inline h-3 w-3 mx-1 text-text-muted" />
          <span className="font-mono text-green-400">{shortPath(dst)}</span>
        </span>
      )
    }
    case 'vps_move_file': {
      const src = (args.source as string) || ''
      const dst = (args.destination as string) || ''
      return (
        <span>
          Rename on VPS:{' '}
          <span className="font-mono text-text-muted">{shortPath(src)}</span>
          <ArrowRight className="inline h-3 w-3 mx-1 text-text-muted" />
          <span className="font-mono text-green-400">{shortPath(dst)}</span>
        </span>
      )
    }
    case 'delete_r2_object': {
      const p = stepPath || (args.path as string) || ''
      return (
        <span>
          Delete <span className="font-mono text-red-400">{shortPath(p)}</span> from R2{' '}
          <span className="text-text-muted">(7-day restore window)</span>
        </span>
      )
    }
    case 'patch_file': {
      const filePath = stepPath || (args.path as string) || ''
      const oldText = args.old_text as string | undefined
      const newText = args.new_text as string | undefined
      return (
        <div className="space-y-1">
          <span>Patch <span className="font-mono text-accent">{shortPath(filePath)}</span> in GitHub</span>
          {oldText && newText && (
            <div className="rounded bg-background px-2.5 py-1.5 space-y-1 text-[10px] font-mono">
              <div className="text-red-400 line-through break-all">{oldText}</div>
              <div className="text-green-400 break-all">{newText}</div>
            </div>
          )}
        </div>
      )
    }
    case 'vps_patch_file': {
      const p = stepPath || (args.path as string) || ''
      return <span>Patch <span className="font-mono text-accent">{shortPath(p)}</span> on VPS</span>
    }
    case 'write_platform_files': {
      const writes = (args.writes ?? {}) as { r2?: { path: string }[]; github?: { path: string }[]; vps?: { path: string }[] }
      const targets = [writes.r2?.length && 'R2', writes.github?.length && 'GitHub', writes.vps?.length && 'VPS'].filter(Boolean).join(', ')
      const firstPath = (writes.r2?.[0] || writes.github?.[0] || writes.vps?.[0])?.path || ''
      return (
        <span>
          Write <span className="font-mono text-accent">{shortPath(firstPath)}</span> to {targets}
        </span>
      )
    }
    case 'write_r2_memory': {
      const p = (args.path as string) || ''
      return <span>Write <span className="font-mono text-accent">{p}</span> to R2</span>
    }
    default:
      return <span>Run <span className="font-mono text-accent">{tool}</span></span>
  }
}

function AcceptPreview({ plan, action, acting, onConfirm, onCancel }: {
  plan: ExecStep[] | null
  action: 'accepted' | 'accepted_pending'
  acting: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const isQueue = action === 'accepted_pending'
  const borderColor = isQueue ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-green-500/30 bg-green-500/5'
  const textColor = isQueue ? 'text-yellow-400' : 'text-green-400'
  const btnColor = isQueue
    ? 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/30'
    : 'bg-green-500/20 border-green-500/30 text-green-400 hover:bg-green-500/30'

  return (
    <div className={`pt-1 rounded-lg border ${borderColor} p-3 space-y-3`}>
      <div className="flex items-center justify-between">
        <span className={`text-[11px] font-semibold ${textColor} uppercase tracking-wider`}>
          {isQueue ? 'Accept & Queue' : plan?.length === 0 ? 'Confirm completion' : 'What will execute'}
        </span>
        <button onClick={onCancel} className="text-text-muted hover:text-text-primary">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {isQueue && (
        <p className="text-xs text-text-secondary">
          No execution plan defined. Accepting queues this for the daily cron (06:00) to investigate and execute. Status will move to <span className="font-mono text-yellow-400">accepted_pending_execution</span> until the cron picks it up.
        </p>
      )}

      {!isQueue && plan !== null && plan.length === 0 && (
        <p className="text-xs text-text-secondary">
          All changes already applied to R2, GitHub, and VPS. Confirming marks this recommendation as completed.
        </p>
      )}

      {!isQueue && plan !== null && plan.length > 0 && (
        <div className="space-y-2">
          {plan.map((step, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-text-secondary">
              <span className={`shrink-0 w-4 ${textColor} font-semibold`}>{i + 1}.</span>
              <StepDetail step={step} />
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onConfirm}
        disabled={acting}
        className={`w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border ${btnColor} text-xs font-semibold disabled:opacity-50 transition-colors`}
      >
        {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
        {isQueue ? 'Accept & Queue' : plan !== null && plan.length > 0 ? 'Confirm & Execute' : 'Confirm'}
      </button>
    </div>
  )
}

function RecCard({ rec, onAction }: { rec: EngReviewRec; onAction: (karId: string, action: string, notes?: string, snoozeUntil?: string) => Promise<void> }) {
  const [expanded, setExpanded] = useState(rec.is_direct_question === 1)
  const [acting, setActing] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingAction | null>(null)

  const priority = PRIORITY_CONFIG[rec.priority] ?? PRIORITY_CONFIG.medium
  const status = STATUS_CONFIG[rec.status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.open
  const catLabel = CATEGORY_LABELS[rec.category] ?? rec.category
  const isOpen = rec.status === 'open'

  const parsedPlan: ExecStep[] | null = (() => {
    if (rec.execution_plan === undefined || rec.execution_plan === null) return null
    try { return JSON.parse(rec.execution_plan) } catch { return null }
  })()

  async function fire(action: string, notes?: string, snoozeUntil?: string) {
    setActing(action)
    setPending(null)
    try {
      await onAction(rec.kar_id, action, notes || undefined, snoozeUntil || undefined)
    } finally {
      setActing(null)
    }
  }

  function startAction(action: string) {
    if (action === 'accepted') {
      setPending({ action: 'accepting', notes: '', snoozeUntil: '' })
    } else if (action === 'declined') {
      fire(action)
    } else if (action === 'snoozed') {
      setPending({ action, notes: '', snoozeUntil: defaultSnoozeDate() })
    } else {
      setPending({ action, notes: '', snoozeUntil: '' })
    }
  }

  return (
    <div className={`rounded-xl border transition-colors ${rec.is_direct_question ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-surface-elevated'}`}>
      <button
        className="w-full flex items-start gap-3 px-4 py-3 text-left"
        onClick={() => setExpanded(e => !e)}
      >
        <span className="mt-0.5 shrink-0 text-text-muted">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <span className="text-[11px] font-mono font-bold text-text-muted">{rec.kar_id}</span>
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${priority.color}`}>{priority.label}</Badge>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-surface text-text-muted border-border">{catLabel}</Badge>
            {rec.is_direct_question === 1 && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-500/15 text-amber-400 border-amber-500/30 flex items-center gap-1">
                <HelpCircle className="h-2.5 w-2.5" />Question
              </Badge>
            )}
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ml-auto ${status.color}`}>{status.label}</Badge>
          </div>
          <p className="text-sm font-medium text-text-primary leading-snug">{rec.title}</p>
          <p className="text-[11px] text-text-muted mt-0.5">{timeSince(rec.created_at)}</p>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-3 border-t border-border/60 pt-3 space-y-3">
          {rec.is_direct_question === 1 && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/25">
              <MessageSquare className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-300">This is a direct question for Dervent — no recommendation can be made without your answer.</p>
            </div>
          )}

          {rec.status === 'completed' && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/25">
              <Zap className="h-3.5 w-3.5 text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-xs text-emerald-300">Executed — action completed successfully.</p>
            </div>
          )}
          {rec.status === 'accepted' && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-green-500/10 border border-green-500/25">
              <CheckCircle className="h-3.5 w-3.5 text-green-400 shrink-0 mt-0.5" />
              <p className="text-xs text-green-300">Accepted — manual follow-through required.</p>
            </div>
          )}
          {rec.status === 'accepted_pending_execution' && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/25">
              <BellOff className="h-3.5 w-3.5 text-yellow-400 shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-300">Queued — accepted and waiting for the daily cron (06:00) to execute.</p>
            </div>
          )}
          {rec.status === 'more_research' && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-purple-500/10 border border-purple-500/25">
              <Search className="h-3.5 w-3.5 text-purple-400 shrink-0 mt-0.5" />
              <p className="text-xs text-purple-300">Additional research required before a decision can be made.</p>
            </div>
          )}
          {rec.status === 'snoozed' && rec.snooze_until && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-gray-500/10 border border-gray-500/25">
              <BellOff className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" />
              <p className="text-xs text-gray-400">Snoozed until {rec.snooze_until.slice(0, 10)} — will resurface automatically.</p>
            </div>
          )}

          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1">Reasoning</p>
            <p className="text-xs text-text-secondary leading-relaxed">{rec.reasoning}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1">Evidence</p>
            <p className="text-xs text-text-secondary leading-relaxed font-mono bg-background rounded px-2.5 py-2 whitespace-pre-wrap break-words">{rec.evidence}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-1">Recommended Action</p>
            <p className="text-xs text-text-secondary leading-relaxed">{rec.recommended_action}</p>
          </div>

          {isOpen && !pending && (
            <div className="pt-1 space-y-2">
              <div className="flex gap-2">
                <button
                  onClick={() => startAction('accepted')}
                  disabled={!!acting}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-green-500/20 border border-green-500/30 text-green-400 text-xs font-semibold hover:bg-green-500/30 disabled:opacity-50 transition-colors"
                >
                  {(acting === 'accepted' || acting === 'accepted_pending') ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                  {parsedPlan === null ? 'Accept & Queue' : 'Accept'}
                </button>
                <button
                  onClick={() => startAction('declined')}
                  disabled={!!acting}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-red-500/10 border border-red-500/25 text-red-400 text-xs font-semibold hover:bg-red-500/20 disabled:opacity-50 transition-colors"
                >
                  {acting === 'declined' ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                  Decline
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => startAction('re_review')}
                  disabled={!!acting}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-border text-text-muted text-xs font-semibold hover:text-text-primary hover:border-accent/30 disabled:opacity-50 transition-colors"
                >
                  {acting === 're_review' ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                  Re-review
                </button>
                <button
                  onClick={() => startAction('more_research')}
                  disabled={!!acting}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-border text-text-muted text-xs font-semibold hover:text-text-primary hover:border-accent/30 disabled:opacity-50 transition-colors"
                >
                  {acting === 'more_research' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                  Research
                </button>
                <button
                  onClick={() => startAction('snoozed')}
                  disabled={!!acting}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-border text-text-muted text-xs font-semibold hover:text-text-primary hover:border-accent/30 disabled:opacity-50 transition-colors"
                >
                  {acting === 'snoozed' ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellOff className="h-3 w-3" />}
                  Snooze
                </button>
              </div>
            </div>
          )}

          {/* Accept preview — two paths: immediate execute (has plan) or queue (no plan) */}
          {isOpen && pending?.action === 'accepting' && (
            <AcceptPreview
              plan={parsedPlan}
              action={parsedPlan === null ? 'accepted_pending' : 'accepted'}
              acting={acting === 'accepted' || acting === 'accepted_pending'}
              onConfirm={() => fire(parsedPlan === null ? 'accepted_pending' : 'accepted')}
              onCancel={() => setPending(null)}
            />
          )}

          {/* Inline confirm form for re_review / more_research / snoozed */}
          {isOpen && pending && pending.action !== 'accepting' && (
            <div className="pt-1 rounded-lg border border-accent/30 bg-accent/5 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-accent uppercase tracking-wider">
                  {pending.action === 'snoozed' ? 'Snooze until' : pending.action === 're_review' ? 'Re-review — what to re-examine?' : 'Research — what needs investigation?'}
                </span>
                <button onClick={() => setPending(null)} className="text-text-muted hover:text-text-primary">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {pending.action === 'snoozed' ? (
                <input
                  type="date"
                  value={pending.snoozeUntil}
                  onChange={e => setPending(p => p ? { ...p, snoozeUntil: e.target.value } : p)}
                  className="w-full text-xs bg-background border border-border rounded px-2.5 py-1.5 text-text-primary focus:outline-none focus:border-accent/50"
                />
              ) : (
                <textarea
                  rows={2}
                  placeholder={pending.action === 're_review' ? 'Describe what aspect needs re-examination...' : 'Describe what research is needed...'}
                  value={pending.notes}
                  onChange={e => setPending(p => p ? { ...p, notes: e.target.value } : p)}
                  className="w-full text-xs bg-background border border-border rounded px-2.5 py-1.5 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent/50 resize-none"
                />
              )}
              <button
                onClick={() => fire(pending.action, pending.notes || undefined, pending.snoozeUntil || undefined)}
                disabled={!!acting || (pending.action === 'snoozed' ? !pending.snoozeUntil : !pending.notes.trim())}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-accent/20 border border-accent/30 text-accent text-xs font-semibold hover:bg-accent/30 disabled:opacity-50 transition-colors"
              >
                {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                Confirm
              </button>
            </div>
          )}

          {!isOpen && rec.actioned_at && (
            <p className="text-[11px] text-text-muted pt-1">
              {status.label} · {timeSince(rec.actioned_at)}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export function KnowledgeAuditPanel() {
  const [statusFilter, setStatusFilter] = useState<string>('open')
  const [priorityFilter, setPriorityFilter] = useState<string>('all')

  const { data, isLoading, error, refetch } = useEngReview(
    statusFilter === 'all' ? undefined : statusFilter,
    priorityFilter === 'all' ? undefined : priorityFilter,
  )
  const { mutateAsync: doAction } = useEngReviewAction()

  const recs = data?.recommendations ?? []

  async function handleAction(karId: string, action: string, notes?: string, snoozeUntil?: string) {
    await doAction({ karId, action, notes, snoozeUntil })
  }

  return (
    <div className="space-y-4">
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: data.total, color: 'text-text-primary' },
            { label: 'Open', value: data.open, color: 'text-accent' },
            { label: 'Executed', value: data.completed ?? 0, color: 'text-emerald-400' },
            { label: 'Snoozed', value: data.snoozed, color: 'text-gray-400' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-center">
              <div className={`text-xl font-bold font-mono ${color}`}>{value}</div>
              <div className="text-[10px] text-text-muted mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            Knowledge Audit
            {isLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            <button
              onClick={() => refetch()}
              className="ml-auto p-1 rounded hover:bg-surface-secondary text-text-muted hover:text-text-primary transition-colors"
              title="Refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </CardTitle>

          <div className="flex flex-wrap gap-1.5 mt-2">
            {STATUS_FILTERS.map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-2.5 py-1 text-[11px] rounded-lg border capitalize transition-colors ${
                  statusFilter === s
                    ? 'bg-accent/20 text-accent border-accent/30'
                    : 'text-text-muted border-border hover:border-accent/30'
                }`}
              >
                {s === 'all' ? 'All'
                  : s === 'more_research' ? 'Research'
                  : s === 'completed' ? 'Executed'
                  : s === 'accepted_pending_execution' ? 'Queued'
                  : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          <div className="flex gap-1.5 mt-1.5">
            {PRIORITY_FILTERS.map(p => (
              <button
                key={p}
                onClick={() => setPriorityFilter(p)}
                className={`px-2.5 py-1 text-[11px] rounded-lg border capitalize transition-colors ${
                  priorityFilter === p
                    ? p === 'high' ? 'bg-red-500/20 text-red-400 border-red-500/30'
                      : p === 'medium' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                      : p === 'low' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                      : 'bg-accent/20 text-accent border-accent/30'
                    : 'text-text-muted border-border hover:border-accent/30'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </CardHeader>

        <CardContent>
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400 mb-4">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Failed to load recommendations
            </div>
          )}

          {recs.length === 0 && !isLoading && !error && (
            <div className="text-center py-10 text-text-muted">
              <ClipboardCheck className="h-8 w-8 mx-auto mb-2 opacity-20" />
              <p className="text-sm">No recommendations for this filter</p>
            </div>
          )}

          <div className="space-y-2">
            {recs.map(rec => (
              <RecCard key={rec.kar_id} rec={rec} onAction={handleAction} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
